<template>
  <woot-modal :show.sync="show" :on-close="onCancel" class="outbound-visible" size="medium">
    <div class="h-auto flex flex-col docal-modal-body">
      <woot-modal-header
        :header-title="$t('DOCAL_MODAL.TITLE')"
        :header-content="$t('DOCAL_MODAL.DESCRIPTION')"
      />
      <div v-if="isLoading" class="docal-loading">
        <spinner />
        <p>{{ $t('DOCAL_MODAL.LOADING') }}</p>
      </div>
      <div v-else class="flex flex-col h-full">
        <div class="bg-white flex-1 docal-table-wrapper">
          <ve-table
            :fixed-header="true"
            :columns="columns"
            :table-data="tableData"
            :border-around="false"
            :sort-option="sortOption"
            :radio-option="radioOption"
            :max-height="400"
            row-key-field-name="rowId"
          />
          <empty-state
            v-if="showEmptyState"
            :title="$t('DOCAL_MODAL.NO_DOCUMENTS_FOUND')" />
        </div>
      </div>
      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <woot-button
          :is-disabled="selectedRowKey === null || isSubmitting"
          @click="onSubmit"
        >
          <spinner v-if="isSubmitting" size="small" />
          {{ isSubmitting ? $t('DOCAL_MODAL.GENERATING_LINK') : $t('DOCAL_MODAL.GENERATE_LINK') }}
        </woot-button>
        <button class="button clear" @click.prevent="onCancel">
          {{ $t('DOCAL_MODAL.CANCEL') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import DocalAPI from '../../../api/docal';

export default {
  name: 'DocalDocumentsModal',
  components: {
    Spinner,
    EmptyState,
    VeTable,
  },
  mixins: [rtlMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
    contactEmail: {
      type: String,
      default: '',
    },
    contactPhone: {
      type: String,
      default: '',
    },
    inboxId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      documents: [],
      isLoading: false,
      isSubmitting: false,
      sortConfig: {},
      selectedRowKey: null,
      sortOption: {
        sortAlways: true,
        sortChange: params => {
          this.sortConfig = params;
          this.applySorting();
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    showEmptyState() {
      return !this.isLoading && this.documents.length === 0;
    },
    radioOption() {
      return {
        selectedRowKey: this.selectedRowKey,
        selectedRowChange: ({ row }) => {
          this.selectedRowKey = row.rowId;
        },
      };
    },
    tableData() {
      if (this.isLoading) return [];
      return this.documents.map((doc, index) => ({
        rowId: index,
        file_name: doc.file_name,
        page_count: doc.page_count != null ? doc.page_count : '',
        created_at_formatted: doc.created_at_formatted || '',
        updated_at_formatted: doc.updated_at_formatted || '',
      }));
    },
    columns() {
      return [
        {
          field: '',
          key: 'radio',
          type: 'radio',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'file_name',
          key: 'file_name',
          sortBy: this.sortConfig.file_name || '',
          title: this.$t('DOCAL_TABLE.FILE_NAME'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            const name = row.file_name;
            if (!name) return '';
            return (
              <span title={name} class="file-name-cell">{name}</span>
            );
          },
        },
        {
          field: 'page_count',
          key: 'page_count',
          title: this.$t('DOCAL_TABLE.PAGES'),
          align: this.isRTLView ? 'right' : 'left',
        },
        {
          field: 'created_at_formatted',
          key: 'created_at_formatted',
          sortBy: this.sortConfig.created_at_formatted || '',
          title: this.$t('DOCAL_TABLE.ADDED_DATE'),
          align: this.isRTLView ? 'right' : 'left',
        },
        {
          field: 'updated_at_formatted',
          key: 'updated_at_formatted',
          sortBy: this.sortConfig.updated_at_formatted || '',
          title: this.$t('DOCAL_TABLE.UPDATED_DATE'),
          align: this.isRTLView ? 'right' : 'left',
        },
      ];
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchDocuments();
      } else {
        this.reset();
      }
    },
  },
  methods: {
    async fetchDocuments() {
      this.isLoading = true;
      this.documents = [];
      this.selectedRowKey = null;
      try {
        const response = await DocalAPI.getDocumentsList(this.accountId);
        if (response.status !== 200) {
          const errorMsg = 'Failed to load documents. Please try again.';
          console.error('Docal documents error:', errorMsg, response.data);
          return;
        }
        this.documents = response.data.documents || [];
      } catch (error) {
        const errorMsg = 'Failed to load documents. Please try again.';
        console.error('Docal documents error:', errorMsg, error);
      } finally {
        this.isLoading = false;
      }
    },
    applySorting() {
      const key = Object.keys(this.sortConfig).find(
        k => this.sortConfig[k] !== ''
      );
      if (!key) return;
      const direction = this.sortConfig[key];
      this.documents.sort((a, b) => {
        const valA = a[key];
        const valB = b[key];
        if (typeof valA === 'string') {
          return direction === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return direction === 'asc' ? valA - valB : valB - valA;
      });
    },
    async onSubmit() {
      if (this.selectedRowKey === null) return;
      const selectedDoc = this.documents[this.selectedRowKey];
      if (!selectedDoc) return;

      this.isSubmitting = true;
      try {
        let response;
        if (this.conversationId) {
          response = await DocalAPI.getSigningLink(
            this.accountId,
            selectedDoc.document_id,
            this.conversationId
          );
        } else if (this.contactEmail) {
          response = await DocalAPI.getSigningLinkByEmail(
            this.accountId,
            selectedDoc.document_id,
            this.contactEmail,
            this.inboxId
          );
        } else if (this.contactPhone) {
          response = await DocalAPI.getSigningLinkByPhone(
            this.accountId,
            selectedDoc.document_id,
            this.contactPhone,
            this.inboxId
          );
        } else {
          console.error('No conversationId, contactEmail or contactPhone provided for signing link');
          return;
        }
        if (response.status === 200 && response.data && response.data.signing_url) {
          this.$emit('signing-link', response.data.signing_url);
          this.onCancel();
        } else {
          console.error('Failed to generate signing-link with response: ', response);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    onCancel() {
      this.$emit('cancel');
    },
    reset() {
      this.documents = [];
      this.selectedRowKey = null;
      this.sortConfig = {};
      this.isSubmitting = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.docal-modal-body {
  overflow: visible !important;
  padding: var(--space-normal);
}

.docal-modal-body .px-8 {
  padding-left: 1rem !important;
}
.docal-modal-body .px-8 {
  padding-bottom: 2rem !important;
}
.docal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-large);
}

.error-message {
  color: var(--r-500);
}

::v-deep td {
  overflow: hidden;
  text-overflow: ellipsis;
}

.ve-table {
  @apply bg-white dark:bg-slate-900;
  padding-left: 1rem;
  &::v-deep {
    .ve-table-container {
      overflow-y: auto !important;
    }
  }
}
::v-deep(.ve-table-sort) {
  line-height: normal;
}
</style>
